import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mic, Square, Upload, Trash2, Loader2 } from "lucide-react";
import { RecordingWaveform } from "./RecordingWaveform";

// Optional admin voice note: record via microphone OR upload an audio file.
// Uploads to /upload (public) and returns the media URL via onChange.
export function VoiceRecorderInput({ value, onChange, testid = "voice-recorder" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [secsLeft, setSecsLeft] = useState(60);
  const [maxSecs, setMaxSecs] = useState(60);
  const [waveStream, setWaveStream] = useState(null);
  const limitRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const micOk = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window !== "undefined" && "MediaRecorder" in window;
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { if (data?.voice_max_announcement_seconds) setMaxSecs(data.voice_max_announcement_seconds); }).catch(() => {});
  }, []);

  const uploadBlob = async (blob, filename) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Voice note added");
    } catch { toast.error("Voice upload failed. Please try again."); }
    finally { setBusy(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setWaveStream(null);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size) await uploadBlob(blob, "voice-note.webm");
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      setWaveStream(stream);
      setSecsLeft(maxSecs);
      clearInterval(limitRef.current);
      limitRef.current = setInterval(() => setSecsLeft((s) => {
        if (s <= 1) { stopRec(); return 0; }
        return s - 1;
      }), 1000);
    } catch { toast.error("Microphone unavailable"); }
  };
  const stopRec = () => { clearInterval(limitRef.current); if (recRef.current && recRef.current.state === "recording") { recRef.current.stop(); } setRecording(false); };
  useEffect(() => () => { clearInterval(limitRef.current); streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);
  const onFile = (e) => { const f = e.target.files?.[0]; if (f) uploadBlob(f, f.name); e.target.value = ""; };

  const full = value ? (value.startsWith("http") ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`) : null;

  return (
    <div data-testid={testid} className="border border-line rounded-lg p-3 bg-sand/40">
      {full && (
        <div className="flex items-center gap-2 mb-2">
          <audio controls src={full} className="h-9 flex-1" data-testid={`${testid}-preview`} />
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`} title="Remove voice note" className="text-critical hover:opacity-70 p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
      {recording && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border border-critical/30 bg-critical/5 px-2.5 py-1.5" data-testid={`${testid}-recording-bar`}>
          <span className="inline-block w-2 h-2 rounded-full bg-critical animate-pulse" />
          <RecordingWaveform stream={waveStream} className="flex-1" />
          <span className="text-xs font-bold tabular-nums text-critical">{secsLeft}s</span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {micOk && (
          <button type="button" onClick={recording ? stopRec : startRec} disabled={busy} data-testid={`${testid}-record`}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold border transition-colors disabled:opacity-50 ${recording ? "bg-critical text-white border-critical animate-pulse" : "bg-white text-clinical border-clinical/30 hover:bg-clinical/5"}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <><Square className="w-4 h-4" /> Stop &amp; save ({secsLeft}s)</> : <><Mic className="w-4 h-4" /> Record</>}
          </button>
        )}
        <label data-testid={`${testid}-upload-label`} className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold border border-line bg-white text-muted2 hover:text-ink cursor-pointer">
          <Upload className="w-4 h-4" /> Upload audio
          <input type="file" accept="audio/*" className="hidden" onChange={onFile} data-testid={`${testid}-file`} />
        </label>
      </div>
      <p className="text-xs text-muted2 mt-2">Optional. Record (max {maxSecs} seconds) or upload a spoken version in any language — visitors can play it alongside the text.</p>
    </div>
  );
}
